import { makeStyles } from '@mui/styles'
import React, { useRef, useState, useMemo, useEffect } from 'react'
import { Popover } from '@mui/material'

import { useSignatureContext } from '@/modules/signature/_helpers/SignatureContext'
import TextIcon from '@assets/images/icons/v2/text-indicator.svg'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'

import ThemeInterface from '@/theme-interface'
import { getContrastText } from '@/modules/base/helpers/getContrastText'
import { TextInput } from '@/modules/base/components/inputs'
import lang from '@/lang'
import { ButtonSquare, CancelButton } from '@/modules/base/components/buttons'
import { useAppSelector } from '@/redux/store'
import MarkerBase from '@/modules/signature/components/overlay/marker-base'

type TextFieldPositionMarkerProps = {
	active: boolean,
	i: number,
	textField: {
		name: string,
		signerEmail?: string,
		position: {
			x: number,
			y: number,
			page: number,
		},
		color?: string,
		text?: string,
		description?: string,
		signed?: boolean,
		signedAt?: string,
	},
}

const useStyles = makeStyles((theme: ThemeInterface) => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
  },
  popover: {
    padding: 30,
  },
  popoverContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 15,
  },
  popoverActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
    marginTop: 15,
  },
  item: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  itemInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
  },
  itemHint: {
    fontSize: theme.fontSizes.extrasmall,
    color: theme.textLight,
    maxWidth: 420,
  },
}), { name: 'TextFieldPositionMarker' })

const TextFieldPositionMarker = (props: TextFieldPositionMarkerProps) => {
  const { textField, active, i } = props

  const inputRef = useRef<HTMLInputElement | null>(null)
  const markerChildRef = useRef<HTMLDivElement>(null)

  const {
    updateItem,
    item: activeTextField,
    signingMode,
    placingMode,
    pager,
    popoverOpenForIndex,
    openRelatedPopoverForIndex,
  } = useSignatureContext()
  const {
    position,
    text: initalText,
    description: initialDescription,
    signed,
    signerEmail,
    color: signatureColor,
  } = textField
  const [popoverOpen, setPopoverOpen] = useState<boolean>(false)

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined

    if (popoverOpenForIndex === i) {
      // necessary to position popover correctly
      timeoutId = setTimeout(() => {
        setPopoverOpen(true)
        openRelatedPopoverForIndex(undefined)
      }, 50)
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [popoverOpenForIndex, openRelatedPopoverForIndex, i])

  const initialState = useMemo(() => ({ text: initalText, description: initialDescription }), [])
  const [data, setData] = useState(initialState)
  const classes = useStyles()

  const userEmail = useAppSelector(state => state.user.info.email)
  const isTaxConsultant = useAppSelector(state => state.tenant.info.isTaxConsultant)

  const handleTextFieldMarkerClick = () => {
    if (!signingMode && !placingMode) { return }
    if (signingMode && (userEmail !== signerEmail && !isTaxConsultant)) { return }
    setPopoverOpen(true)
  }

  const handleSubmit = () => {
    updateItem({ ...activeTextField, ...data })
    setPopoverOpen(false)
  }
  const handleBlur = (value, name) => {
    setData({ ...data, [name]: value })
  }

  if (!pager?.currentPage || pager.currentPage !== position.page || signed) return null

  return (
    <MarkerBase
      i={i}
      active={active}
      position={position}
      signed={signed}
      signatureColor={signatureColor}
      onClick={handleTextFieldMarkerClick}
      ref={markerChildRef}
      styleConfig={{ scaled: true }}>
      <>
        <SVGIcon
          svg={TextIcon}
          height="13px"
          width="13px"
          fill={getContrastText(signatureColor)} />
        <div className={classes.content}>
          <span>{initialDescription || lang.signature.textLabel}</span>
        </div>
        {popoverOpen && (
          <Popover
            open={popoverOpen}
            onClose={() => setPopoverOpen(false)}
            slotProps={{
              transition: {
                onEntered: () => inputRef.current?.focus(),
              },
            }}
            anchorEl={markerChildRef.current}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            classes={{ paper: classes.popover }}>
            <div className={classes.popoverContent}>
              {placingMode && (
                <div className={classes.item}>
                  <div className={classes.itemInfo}>
                    <label>{lang.signature.textField.description.label}</label>
                    <div className={classes.itemHint}>{lang.signature.textField.description.hint}</div>
                  </div>
                  <TextInput
                    ref={placingMode ? inputRef : null}
                    name='description'
                    placeholder={lang.signature.textField.description.placeholder}
                    value={data.description}
                    onBlur={handleBlur}
                    useFormik={false}
                    autoComplete='off'
                    dataQsId='signature_text_description_input' />
                </div>
              )}
              <div className={classes.item}>
                <div className={classes.itemInfo}>
                  <label>{signingMode ? (initialDescription || lang.signature.textField.text.defaultLabel) : lang.signature.textField.text.label}</label>
                  {placingMode && (
                    <div className={classes.itemHint}>
                      {lang.signature.textField.text.hint}
                    </div>
                  )}
                </div>
                <TextInput
                  name='text'
                  placeholder={placingMode ? lang.signature.textField.text.placeholder : ''}
                  ref={!placingMode ? inputRef : null}
                  value={data.text}
                  onBlur={handleBlur}
                  useFormik={false}
                  autoComplete='off'
                  dataQsId='signature_text_input' />
              </div>
            </div>
            <div className={classes.popoverActions}>
              <CancelButton label={lang.generics.abort} onClick={() => {
                setPopoverOpen(false)
                setData(initialState)
              }} />
              <ButtonSquare label={lang.generics.apply} variant='small' onClick={handleSubmit} />
            </div>
          </Popover>
        )}
      </>
    </MarkerBase>
  )
}

export default TextFieldPositionMarker
